/**
 * AssetServiceTypesConfigPage.jsx
 *
 * Settings page for managing Asset Service Types.
 * Service types are named categories of scheduled maintenance
 * (e.g. "Major Service", "Minor Service", "Fire Safety Inspection", "Electrical COC").
 *
 * Admins can:
 *   - View all active service types
 *   - Create new types with name, description, colour, icon, and optional category scope
 *   - Edit existing types
 *   - Deactivate (soft-delete) types
 *
 * @module SettingsPages/AssetServiceTypesConfigPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaCogs, FaPlus, FaEdit, FaTrashAlt, FaSpinner,
    FaTimes, FaSave, FaTag, FaExclamationTriangle
} from 'react-icons/fa';
import '@/styles/ModuleTemplateConfigStyles.scss';
import * as Sentry from '@sentry/react';

const INTERVAL_UNITS = ['Days', 'Weeks', 'Months', 'Years'];

const PRESET_COLOURS = [
    '#f63040', '#e67e22', '#f1c40f', '#2ecc71',
    '#3498db', '#9b59b6', '#1abc9c', '#e74c3c',
    '#34495e', '#7f8c8d'
];

const emptyForm = () => ({
    name: '',
    description: '',
    colour: '#f63040',
    icon: 'fa-wrench',
    assetCategoryId: '',
    sortOrder: 0
});

/**
 * AssetServiceTypesConfigPage
 */
const AssetServiceTypesConfigPage = () => {
    const { apiFetch } = useAuth();

    const [serviceTypes, setServiceTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // ── Data loading ─────────────────────────────────────────────────────────

    const fetchServiceTypes = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/service-types');
            if (res.ok) {
                const data = await res.json();
                setServiceTypes(data.data ?? []);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load service types');
        }
    }, [apiFetch]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/categories');
            if (res.ok) {
                const data = await res.json();
                setCategories(data.data ?? []);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    }, [apiFetch]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        Promise.all([fetchServiceTypes(), fetchCategories()])
            .finally(() => setLoading(false));
    }, [fetchServiceTypes, fetchCategories]);

    // ── Modal helpers ────────────────────────────────────────────────────────

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setShowModal(true);
    };

    const openEdit = (type) => {
        setEditingId(type.assetServiceTypeId);
        setForm({
            name: type.name ?? '',
            description: type.description ?? '',
            colour: type.colour ?? '#f63040',
            icon: type.icon ?? 'fa-wrench',
            assetCategoryId: type.assetCategoryId ?? '',
            sortOrder: type.sortOrder ?? 0
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Service type name is required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                colour: form.colour || null,
                icon: form.icon.trim() || null,
                assetCategoryId: form.assetCategoryId ? parseInt(form.assetCategoryId) : null,
                sortOrder: parseInt(form.sortOrder) || 0
            };

            const url = editingId
                ? `/api/AssetManagement/service-types/${editingId}`
                : '/api/AssetManagement/service-types';
            const method = editingId ? 'PUT' : 'POST';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success(editingId ? 'Service type updated' : 'Service type created');
                closeModal();
                fetchServiceTypes();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message || 'Failed to save service type');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to save service type');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────

    const handleDelete = async (id) => {
        try {
            const res = await apiFetch(`/api/AssetManagement/service-types/${id}`, { method: 'DELETE' });
            if (res.ok) {
                toast.success('Service type deactivated');
                setDeleteConfirmId(null);
                fetchServiceTypes();
            } else {
                toast.error('Failed to deactivate service type');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to deactivate service type');
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="module-template-config-page">
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <FaSpinner className="fa-spin" size={32} color="#f63040" />
                </div>
            </div>
        );
    }

    return (
        <div className="module-template-config-page">
            {/* Page Header */}
            <div className="page-header">
                <div className="header-left">
                    <h1><FaCogs /> Asset Service Types</h1>
                    <p className="page-description">
                        Configure named service types for assets — such as Major Service, Minor Service,
                        Fire Safety Inspection, or Electrical COC. These types are used when setting up
                        service interval workflows on individual assets or asset groups.
                    </p>
                </div>
                <div className="header-actions">
                    <button className="btn-primary" onClick={openCreate}>
                        <FaPlus /> New Service Type
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="module-group" style={{ marginTop: 0 }}>
                {serviceTypes.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                        <FaCogs size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                        <p>No service types configured yet.</p>
                        <p style={{ fontSize: '0.9rem' }}>
                            Click <strong>New Service Type</strong> to define service categories
                            for your asset maintenance workflows.
                        </p>
                    </div>
                ) : (
                    <table className="settings-table" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}></th>
                                <th>NAME</th>
                                <th>DESCRIPTION</th>
                                <th>CATEGORY SCOPE</th>
                                <th>SORT ORDER</th>
                                <th style={{ width: 100 }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {serviceTypes.map(st => {
                                const catName = st.assetCategoryId
                                    ? categories.find(c => c.assetCategoryId === st.assetCategoryId)?.name
                                    : null;
                                return (
                                    <tr key={st.assetServiceTypeId}>
                                        <td>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: '50%',
                                                    background: st.colour || '#aaa',
                                                    border: '1px solid rgba(0,0,0,0.15)'
                                                }}
                                                title={st.colour || 'No colour'}
                                            />
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{st.name}</td>
                                        <td style={{ color: '#718096', fontSize: '0.9rem' }}>
                                            {st.description || <em style={{ opacity: 0.5 }}>—</em>}
                                        </td>
                                        <td>
                                            {catName
                                                ? <span className="tag-badge">{catName}</span>
                                                : <em style={{ opacity: 0.5, fontSize: '0.85rem' }}>All categories</em>
                                            }
                                        </td>
                                        <td>{st.sortOrder}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button
                                                    className="btn-icon"
                                                    title="Edit"
                                                    onClick={() => openEdit(st)}
                                                >
                                                    <FaEdit />
                                                </button>
                                                {deleteConfirmId === st.assetServiceTypeId ? (
                                                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                        <button
                                                            className="btn-danger-sm"
                                                            onClick={() => handleDelete(st.assetServiceTypeId)}
                                                            title="Confirm deactivate"
                                                        >
                                                            <FaExclamationTriangle /> Yes
                                                        </button>
                                                        <button
                                                            className="btn-secondary-sm"
                                                            onClick={() => setDeleteConfirmId(null)}
                                                        >
                                                            No
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <button
                                                        className="btn-icon btn-icon-danger"
                                                        title="Deactivate"
                                                        onClick={() => setDeleteConfirmId(st.assetServiceTypeId)}
                                                    >
                                                        <FaTrashAlt />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create / Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div
                        className="modal-container"
                        style={{ maxWidth: 520 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h2>{editingId ? 'Edit Service Type' : 'New Service Type'}</h2>
                            <button className="modal-close" onClick={closeModal}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            {/* Name */}
                            <div className="form-group">
                                <label className="form-label">Name <span style={{ color: '#f63040' }}>*</span></label>
                                <input
                                    className="form-input"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. Major Service, Fire Safety Inspection"
                                />
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-input"
                                    rows={3}
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Optional — describe what this service entails"
                                />
                            </div>

                            {/* Colour */}
                            <div className="form-group">
                                <label className="form-label">Colour</label>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {PRESET_COLOURS.map(c => (
                                        <span
                                            key={c}
                                            onClick={() => setForm(f => ({ ...f, colour: c }))}
                                            style={{
                                                display: 'inline-block',
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                background: c,
                                                cursor: 'pointer',
                                                border: form.colour === c ? '3px solid #2d3748' : '2px solid transparent',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    ))}
                                    <input
                                        type="color"
                                        value={form.colour || '#f63040'}
                                        onChange={e => setForm(f => ({ ...f, colour: e.target.value }))}
                                        style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                        title="Custom colour"
                                    />
                                </div>
                            </div>

                            {/* Category scope */}
                            <div className="form-group">
                                <label className="form-label">Category Scope</label>
                                <select
                                    className="form-select"
                                    value={form.assetCategoryId}
                                    onChange={e => setForm(f => ({ ...f, assetCategoryId: e.target.value }))}
                                >
                                    <option value="">All Categories</option>
                                    {categories.filter(c => c.isActive).map(c => (
                                        <option key={c.assetCategoryId} value={c.assetCategoryId}>{c.name}</option>
                                    ))}
                                </select>
                                <small style={{ color: '#718096', marginTop: 4, display: 'block' }}>
                                    Limit this service type to assets in a specific category, or leave blank for all.
                                </small>
                            </div>

                            {/* Sort Order */}
                            <div className="form-group">
                                <label className="form-label">Sort Order</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={form.sortOrder}
                                    onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
                                    min={0}
                                    style={{ width: 100 }}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeModal} disabled={saving}>
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? <><FaSpinner className="fa-spin" /> Saving…</> : <><FaSave /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetServiceTypesConfigPage;
